import { UserModel, RoleModel, ProjectRole } from "../types/UserModel";
import appClient, { ResultApi } from "../../common/services/AppClient";
import { ResponseData } from "../../common/services/AppClient";
import security from "../../common/services/SecurityService";
import { Utils } from "../../common/misc/Utils";
import { CustomRole } from "../types/CustomRoleModel";
import {
    MetadataDefinition,
    CalculationConfig,
    MetadataCalculationHandlingMode,
    MetadataImportOverride,
    MetadataImportPreviewResult,
} from "../types/Metadata";
import { ActionRole } from "../types/Actions";
import { DashboardWidgetMetaResult } from "../types/DashboardWidgetMeta";
import { ALL_PROJECTS } from "src/modules/tasks/screens/TasksPage";
import { ListItem } from "src/modules/common/types/ListItem";
import { endpoints } from "../../../config";

export class GlobalAdministrationService {
    async getRoles(): Promise<RoleModel[]> {
        const url = endpoints.tasksUrl + "users/roles";
        const resp = await appClient.get<RoleModel[]>(url);

        return resp.unwrapOr(Array<RoleModel>());
    }

    async getAppUsers(): Promise<UserModel[]> {
        const url = `${endpoints.tasksUrl}users`;
        const resp = await appClient.get<UserModel[]>(url);
        return resp.unwrapOr(Array<UserModel>());
    }

    async getActionRoles(projectId?: string): Promise<ActionRole[]> {
        const url = projectId
            ? `${endpoints.tasksUrl}actions/roles/project/${projectId}`
            : `${endpoints.tasksUrl}actions/roles`;
        const resp = await appClient.get<ActionRole[]>(url);
        return resp.unwrapOr(Array<ActionRole>());
    }

    createActionRole(projectId: string, name: string, description?: string) {
        const url = `${endpoints.tasksUrl}actions/roles`;
        return appClient.postRaw(url, {
            projectId,
            name,
            description: description ?? null,
        });
    }

    updateActionRole(actionRoleId: string, name: string, description?: string) {
        const url = `${endpoints.tasksUrl}actions/roles/${actionRoleId}`;
        return appClient.patchRaw(url, {
            name,
            description: description ?? null,
        });
    }

    deleteActionRole(actionRoleId: string) {
        const url = `${endpoints.tasksUrl}actions/roles/${actionRoleId}`;
        return appClient.deleteRaw(url);
    }

    async createUpdateUser(userData: UserModel): Promise<ResponseData> {
        const url = `${endpoints.tasksUrl!}users`;
        return appClient.postRaw(url, userData);
    }

    async updateProjectAssignments(
        userId: string,
        assignments: ProjectRole[],
    ): Promise<ResponseData> {
        const url = `${endpoints.tasksUrl!}admin/assignments/update/${userId}`;
        return appClient.patchRaw(url, assignments);
    }

    async deleteUser(id: string, isDomainUser: boolean) {
        const url = `${endpoints.tasksUrl!}users/${id}/${isDomainUser}`;
        return appClient.delete(url);
    }

    async resetPassword(
        password: string,
        userId?: string,
    ): Promise<ResponseData> {
        const data = { password: password, userId: userId };
        const url = endpoints.tasksUrl + "userProfile/password";
        return appClient.postRaw(url, data);
    }

    async changePassword(
        newPassword: string,
        oldPassword: string,
        userId?: string | null,
    ): Promise<ResponseData> {
        const data = { userId, oldPassword, newPassword };
        const url = endpoints.tasksUrl + "userProfile/password_change";
        return appClient.postRaw(url, data);
    }

    async changeObjectRoleAssignments(
        userId: string,
        objectId: string,
        objectType: string,
        roles: string[],
    ): Promise<ResponseData> {
        const data = { userId, objectId, objectType, roles };
        const url = endpoints.tasksUrl + "users/object-role-assignments/change";
        return appClient.postRaw(url, data);
    }

    async getCustomRoles(): Promise<CustomRole[]> {
        const url = endpoints.tasksUrl + "customRolesManagement";
        const resp = await appClient.get<CustomRole[]>(url);
        return resp.unwrapOr(Array<CustomRole>());
    }

    async createCustomRole(name: string, permissions: string[]) {
        const data = { name, permissions };
        const url = endpoints.tasksUrl + "customRolesManagement";
        return appClient.postRaw(url, data);
    }

    async updateCustomRole(id: string, name: string, permissions: string[]) {
        const data = { id, name, permissions };
        const url = endpoints.tasksUrl + "customRolesManagement";
        return appClient.updateRaw(url, data);
    }

    async deleteCustomRole(id: string) {
        const url = endpoints.tasksUrl + `customRolesManagement/${id}`;
        return appClient.deleteRaw(url);
    }

    async updateAlphaConfigValue(data: object) {
        const url = `${endpoints.tasksUrl}alpha-configs`;
        return appClient.post(url, { data });
    }

    async updateAlphaSettingsValue(key: string, value: unknown) {
        const data = { key, value };
        const url = `${endpoints.tasksUrl}alpha-settings`;
        return appClient.post(url, data);
    }

    async deleteAlphaSettingsValue(key: string) {
        const url = `${endpoints.tasksUrl}alpha-settings/${key}`;
        return appClient.delete(url);
    }

    async getMetadataDefinitionsForProject(
        projectId?: string,
    ): Promise<ResultApi<MetadataDefinition[]>> {
        const queryParams = projectId ? `?projectId=${projectId}` : "";
        const url = `${endpoints.tasksUrl}metadata${queryParams}`;
        return appClient.get<MetadataDefinition[]>(url);
    }

    createMetadataDefinition(formData: FormData) {
        const url = endpoints.tasksUrl + "metadata/create";
        return appClient.post<string>(url, formData);
    }

    importMetadataDefinitions(
        projectId: string,
        formData: FormData,
    ): Promise<ResultApi<string[]>> {
        const url = `${endpoints.tasksUrl}metadata/import/${projectId}`;
        return appClient.post<string[]>(url, formData);
    }

    previewMetadataImport(
        projectId: string,
        formData: FormData,
    ): Promise<ResultApi<MetadataImportPreviewResult>> {
        const url = `${endpoints.tasksUrl}metadata/import/preview/${projectId}`;
        return appClient.post<MetadataImportPreviewResult>(url, formData);
    }

    createMetadataImportFormData(
        file: File,
        overrides: MetadataImportOverride[] = [],
    ) {
        const formData = new FormData();
        formData.append("file", file);
        if (overrides.length > 0) {
            formData.append("overridesJson", JSON.stringify(overrides));
        }

        return formData;
    }

    deleteMetadataDefinition(metadataDefinitionId: string) {
        const url =
            endpoints.tasksUrl + `metadata/delete/${metadataDefinitionId}`;
        return appClient.delete(url);
    }

    updateMetadataDefinition(
        metadataDefinitionId: string,
        title: string,
        description: string | undefined,
        format: string,
        isVisible: boolean,
        isEditable: boolean,
        isMultiValue: boolean,
        taskTypes: string[],
        listValues: ListItem[],
        calculation: CalculationConfig | undefined,
    ) {
        const request = {
            metadataDefinitionId,
            title,
            description,
            format,
            isVisible,
            isEditable,
            isMultiValue,
            taskTypes,
            listValues,
            calculation,
        };
        const url =
            endpoints.tasksUrl + `metadata/update/${metadataDefinitionId}`;
        return appClient.patch(url, request);
    }

    updateMetadataListOrder(
        id: string,
        dragIndex: number,
        dropIndex: number,
        lastModified: string | null,
    ) {
        const request = {
            dragIndex,
            dropIndex,
            lastModified,
        };
        const url = endpoints.tasksUrl + `metadata/position/update/${id}`;
        return appClient.patch(url, request);
    }

    createUpdateDashboardWidget(formData: object) {
        const url = endpoints.tasksUrl + "admin/widget/create";
        return appClient.update<string>(url, formData);
    }

    createUpdateDashboardGroupedWidget(formData: object) {
        const url = endpoints.tasksUrl + "admin/grouped-widget/create";
        return appClient.update<string>(url, formData);
    }

    async getDashboardWidgetsMeta() {
        const url = `${endpoints.tasksUrl}dashboard/widgets/meta`;
        const resp = await appClient.get<DashboardWidgetMetaResult[]>(url);
        if (resp.isErr()) {
            return [];
        }
        const widgets = resp.value ?? [];
        for (const res of widgets) {
            res.projectId = res.projectId ?? ALL_PROJECTS;
        }
        return widgets;
    }

    deleteWidget(id: string) {
        const url = endpoints.tasksUrl + `admin/widget/delete/${id}`;
        return appClient.delete(url);
    }

    async exportMetadataDefinitions(
        projectId: string,
        ids: string[],
        calculationHandlingMode?: MetadataCalculationHandlingMode,
    ): Promise<void> {
        if (ids.length === 0) return;
        if (ids.length > 1) {
            await this.exportMetadataDefinitionsAsZip(
                projectId,
                ids,
                calculationHandlingMode,
            );
        } else {
            await this.exportSingleMetadataDefinition(
                projectId,
                ids[0],
                calculationHandlingMode,
            );
        }
    }

    private async exportSingleMetadataDefinition(
        projectId: string,
        id: string,
        calculationHandlingMode?: MetadataCalculationHandlingMode,
    ): Promise<void> {
        const mapForm = document.createElement("form");
        const formId = `metadataExportForm_${Date.now()}`;
        mapForm.setAttribute("id", formId);
        mapForm.target = "_blank";
        mapForm.method = "POST";

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "id";
        input.value = id;
        mapForm.appendChild(input);

        if (calculationHandlingMode) {
            const calculationModeInput = document.createElement("input");
            calculationModeInput.type = "hidden";
            calculationModeInput.name = "calculationHandlingMode";
            calculationModeInput.value = calculationHandlingMode;
            mapForm.appendChild(calculationModeInput);
        }

        await security.invoke((token) => {
            const t = "?access_token=" + encodeURIComponent(token);
            const url = endpoints.tasksUrl + `metadata/export/${projectId}${t}`;
            Utils.downloadFile(url, mapForm, formId);
            return Promise.resolve();
        });
    }

    private async exportMetadataDefinitionsAsZip(
        projectId: string,
        ids: string[],
        calculationHandlingMode?: MetadataCalculationHandlingMode,
    ): Promise<void> {
        const mapForm = document.createElement("form");
        const formId = `metadataExportZipForm_${Date.now()}`;
        mapForm.setAttribute("id", formId);
        mapForm.target = "_blank";
        mapForm.method = "POST";

        ids.forEach((id) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = "ids";
            input.value = id;
            mapForm.appendChild(input);
        });

        if (calculationHandlingMode) {
            const calculationModeInput = document.createElement("input");
            calculationModeInput.type = "hidden";
            calculationModeInput.name = "calculationHandlingMode";
            calculationModeInput.value = calculationHandlingMode;
            mapForm.appendChild(calculationModeInput);
        }

        await security.invoke((token) => {
            const t = "?access_token=" + encodeURIComponent(token);
            const url =
                endpoints.tasksUrl + `metadata/export-zip/${projectId}${t}`;
            Utils.downloadFile(url, mapForm, formId);
            return Promise.resolve();
        });
    }
}
